import React, { useState, useRef, memo, useEffect } from 'react';
import { css, useTheme } from '@emotion/react';
import type { MultiCaseSelectorEntryType, ProductProps } from 'tsconfig/types';

import { useScreenLayout } from 'common-ui';

import { scrollToRef } from '../../../utils';
import useProductsData from '../../../hooks/useProductsData';
import { useUpsellProduct } from './hooks/useUpsellProduct';
import { buildCartItems } from './hooks/useCartBuilder';

import { MainMediaSection } from './components/MainMediaSection';
import { ProductDetailsSection } from './components/ProductDetailsSection';
import { ReferencedCaseTabPanel } from './components/ReferencedCaseTabPanel';

export interface CmsMultiCaseSelectorProps {
  contentItem: MultiCaseSelectorEntryType;
}

export const CmsMultiCaseSelector = memo(({ contentItem }: CmsMultiCaseSelectorProps) => {
  const theme = useTheme();
  const targetRef = useRef<HTMLDivElement>(null);
  const cssMain = css`
    ${theme.widgets?.CmsMultiCaseSelector?.main}
  `;
  const cssInfo = css`
    ${theme.widgets?.CmsMultiCaseSelector?.info}
  `;
  const caseCss = css`
    ${theme.widgets.Product?.case}
  `;

  const { image, productCodes, upsellProductCode, eyebrow, headline, subheadline, paragraph } =
    contentItem.fields || {};

  const [activeCase, setActiveCase] = useState(0);
  const [activeBottomCase, setActiveBottomCase] = useState(0);
  const [activeProduct, setActiveProduct] = useState<ProductProps | null>(null);
  const [referencedSkus, setReferencedSkus] = useState<any[]>([]);
  const [upsellSelected, setUpsellSelected] = useState(true);
  const [cartItems, setCartItems] = useState<any>([]);

  const upsellProduct = useUpsellProduct(upsellProductCode);

  const { products } = useProductsData(productCodes);
  const { isMobileView } = useScreenLayout();

  const handleCaseClick = (index: number) => {
    const product = products[index];
    if (!product) {
      return;
    }

    setActiveCase(index);
    setActiveBottomCase(index);
    setActiveProduct(product);
    setReferencedSkus(product.skus?.[0]?.referencedSkus ?? []);
    setCartItems(buildCartItems(product, upsellProduct, upsellSelected));
  };

  const handleUpsellClick = () => {
    const next = !upsellSelected;
    setUpsellSelected(next);
    setActiveBottomCase(next ? products.length : activeCase);
    setReferencedSkus((next ? upsellProduct : products[activeCase])?.skus?.[0]?.referencedSkus ?? []);
    setCartItems(buildCartItems(activeProduct, upsellProduct, next));
  };

  const handleViewTabPanelClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    scrollToRef(targetRef);
  };

  useEffect(() => {
    setActiveProduct(products[0]);
    setCartItems(buildCartItems(products[0], upsellProduct, upsellSelected));
  }, [products]);

  useEffect(() => {
    if (activeProduct) {
      setReferencedSkus(activeProduct?.skus?.[0]?.referencedSkus ?? []);
    }
  }, [activeProduct]);

  if (!products.length) {
    return null;
  }

  return (
    <div className="mcs">
      <div className="mcs-main" css={cssMain}>
        <MainMediaSection
          image={image ?? null}
          eyebrow={eyebrow ?? null}
          paragraph={paragraph ?? null}
          isMobileView={isMobileView}
        />
        <ProductDetailsSection
          eyebrow={eyebrow}
          paragraph={paragraph ?? null}
          headline={headline}
          subheadline={subheadline}
          activeProduct={activeProduct}
          upsellProduct={upsellProduct}
          upsellSelected={upsellSelected}
          products={products}
          activeCase={activeCase}
          cartItems={cartItems}
          isMobileView={isMobileView}
          handleCaseClick={handleCaseClick}
          handleUpsellClick={handleUpsellClick}
          handleViewTabPanelClick={handleViewTabPanelClick}
        />
      </div>

      <div className="mcs-case-info" css={[caseCss, cssInfo]}>
        <ReferencedCaseTabPanel
          targetRef={targetRef}
          activeBottomCase={activeBottomCase}
          products={products}
          upsellProduct={upsellProduct}
          upsellSelected={upsellSelected}
          referencedSkus={referencedSkus}
          handleCaseClick={handleCaseClick}
          handleUpsellClick={handleUpsellClick}
          isMobileView={isMobileView}
        />
      </div>
    </div>
  );
});
